import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Messages } from "../../../layout/Messages/Messages";
import { onEnter, onExit } from "../../../../config/customModalFunctions";
import { NOTIFICATION_CHANGE_PACKAGE_GROUP_MODAL } from "../../../../config/notifications";
import {
  getOrganizationPackageGroups,
  transferPackageGroup
} from "../../../../api/clients/packages";

function ChangePackageGroupModal(props) {
  const { showModal, closeModal, item, actionCallback } = props;
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const intl = useIntl();

  const currentGroupId = item?.package_group?.id || item?.package_group?.uuid;
  const currentGroupName = item?.package_group?.name;

  useEffect(() => {
    if (!showModal || !item?.organization?.id) {
      return;
    }

    setSearch("");
    setSelectedGroupId(null);
    setLoading(true);

    getOrganizationPackageGroups({
      organizationId: item.organization.id,
      forTransferPackage: item.uuid,
      notificationId: NOTIFICATION_CHANGE_PACKAGE_GROUP_MODAL,
      hideNotification: true,
    })
      .then((response) => {
        const packageGroups = response?.data ?? [];
        const currentGroup = packageGroups.find((group) => {
          if (currentGroupId) {
            return group.id === currentGroupId;
          }

          return currentGroupName && group.name === currentGroupName;
        });

        const availableGroups = packageGroups
          .filter((group) => {
            if (currentGroupId) {
              return group.id !== currentGroupId;
            }

            return !currentGroupName || group.name !== currentGroupName;
          })
          .map((group) => ({
            ...group,
            disabled: false,
          }));

        setGroups([
          ...(currentGroup ? [{ ...currentGroup, isCurrentGroup: true, disabled: true }] : [{
            id: currentGroupId || "__current_group__",
            uuid: currentGroupId || "__current_group__",
            name: currentGroupName,
            isCurrentGroup: true,
            disabled: true,
          }].filter((group) => group.name)),
          ...availableGroups,
        ]);
      })
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [showModal, item, currentGroupId, currentGroupName]);

  const filteredGroups = groups.filter((group) => {
    return group.disabled || group.name.toLowerCase().includes(search.toLowerCase());
  });

  const handleSave = () => {
    if (!selectedGroupId) {
      return;
    }

    setSaving(true);

    transferPackageGroup({
      packageId: item.uuid,
      newPackageGroupId: selectedGroupId,
      notificationId: NOTIFICATION_CHANGE_PACKAGE_GROUP_MODAL,
    })
      .then((response) => {
        if (response?.data?.id || response?.data?.uuid) {
          if (actionCallback) {
            actionCallback(true);
          }
          closeModal();
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      isOpen={showModal}
      toggle={closeModal}
      size="md"
      onEnter={onEnter}
      onExit={onExit}
      centered
      returnFocusAfterClose={false}
    >
      <ModalHeader className="pb-0" toggle={closeModal}>
        <FormattedMessage id="components.pages.change_package_group.modal.title" />
      </ModalHeader>

      <ModalBody>
        <Messages notificationId={NOTIFICATION_CHANGE_PACKAGE_GROUP_MODAL} />

        <p className="text-muted small mb-2">
          <FormattedMessage id="components.pages.change_package_group.modal.description" />
        </p>

        <div className="justify-content-start align-items-center mb-4 mt-4 d-flex w-100">
          <FontAwesomeIcon size="lg" className="ml-2 text-muted" icon={faSearch} />
          <input
            className="form-control form-control-sm mx-2"
            type="text"
            maxLength="150"
            placeholder={intl.formatMessage({
              id: "components.layout.paginator.searchTable.searchText",
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </div>

        <ListGroup className="list change-owner-list">
          {!loading && filteredGroups.length === 0 && (
            <ListGroupItem className="custom-list-item">
              <FormattedMessage id="messages.no_data" />
            </ListGroupItem>
          )}
          {!loading && filteredGroups.map((group) => {
            const isSelected = selectedGroupId === group.id;

            return (
              <ListGroupItem
                key={group.id}
                className={`custom-list-item${group.disabled ? " disabled text-muted" : " cursor-pointer"}${isSelected ? " active" : ""}`}
                onClick={group.disabled ? undefined : () => setSelectedGroupId(group.id)}
                action={!group.disabled}
              >
                <div className="d-flex align-items-center justify-content-between w-100 change-owner-list__item-content">
                  <div className={`font-weight-bold change-owner-list__name${group.disabled ? " text-muted" : ""}`}>
                    {group.name}
                    {group.isCurrentGroup && (
                      <span className="badge badge-secondary ml-2 change-owner-list__badge">
                        <FormattedMessage id="components.pages.change_package_group.modal.current_group_badge" />
                      </span>
                    )}
                  </div>
                  {isSelected && !group.disabled && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="ml-3 flex-shrink-0"
                      size="lg"
                    />
                  )}
                </div>
              </ListGroupItem>
            );
          })}
        </ListGroup>
      </ModalBody>

      <ModalFooter>
        <div className="text-center text-sm-right py-3 custom-disabled-buttons-modal">
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || !selectedGroupId}
          >
            <FormattedMessage id="button.save" />
          </Button>
        </div>
        <Button color="secondary" onClick={closeModal} disabled={saving}>
          <FormattedMessage id="button.cancel" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { ChangePackageGroupModal };
